import { useEffect, useRef } from 'react';

import * as echarts from 'echarts/core';
import {
  BarChart,
  FunnelChart,
  GaugeChart,
  HeatmapChart,
  LineChart,
  PieChart,
  RadarChart,
  ScatterChart,
  SunburstChart,
  TreemapChart,
} from 'echarts/charts';
import {
  AriaComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  RadarComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts';

/**
 * The one place ECharts is touched.
 *
 * Two deliberate choices:
 *
 * **SVG, not canvas.** The Superset dashboards this app replaces rendered to
 * canvas, which meant chart internals were unreachable from CSS — every styling
 * problem had to be solved at the chart-config layer, and several never were.
 * SVG output keeps the marks in the DOM: inspectable, selectable, printable,
 * and crisp at any zoom.
 *
 * **Tree-shaken imports.** Only the four chart types and five components below
 * ship. `import * as echarts from 'echarts'` would pull in the whole library —
 * maps, graphs, calendars, the lot — for a bundle that renders bars and lines.
 */
echarts.use([
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  HeatmapChart,
  TreemapChart,
  RadarChart,
  FunnelChart,
  GaugeChart,
  SunburstChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  RadarComponent,
  VisualMapComponent,
  AriaComponent,
  SVGRenderer,
]);

interface Props {
  option: EChartsOption;
  /** Describes the chart for screen readers and for `title` on the SVG. */
  ariaLabel: string;
  className?: string;
}

export const EChart = ({ option, ariaLabel, className }: Props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const chart = echarts.init(host, undefined, { renderer: 'svg' });
    chartRef.current = chart;

    // ECharts does not watch its own container. A dashboard tile changes size
    // when the platform's grid reflows, when the iframe resizes, and when the
    // browser zooms — all of which are container-box changes, not window
    // resizes, so a window listener would miss most of them.
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(host);

    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // notMerge, because a theme flip or a new query can remove series that a
    // merge would leave behind as ghosts.
    chart.setOption({ ...option, aria: { enabled: true, label: { description: ariaLabel } } }, {
      notMerge: true,
    });
  }, [option, ariaLabel]);

  return <div ref={hostRef} className={className ?? 'echart'} role="img" aria-label={ariaLabel} />;
};
